import './PageLayout.scss';

import {
  BlockCta,
  Blocks,
  FeaturedCompany,
  HowDoesItWork,
  TestimonialItem,
} from '@travelwin/cms/types';
import {
  Button,
  ButtonV2,
  ButtonV2Props,
  Country,
  Hero,
  HeroProps,
  HighlightBox,
  ImageWithBackgroundBlock,
  InfoBanner,
  ProductOffering,
  Title,
  sortProductOfferingsBy,
} from '@travelwin/core';
import classNames from 'classnames';
import { FC } from 'react';
import { To } from 'react-router';

import { useAnalytics } from '@/analytics';
import { DynamicPlansSection } from '@/components/DynamicPlansSection';
import Footer from '@/components/Footer';
import { ImageMedia } from '@/components/ImageMedia';
import { Layout } from '@/components/Layout';
import { Link, useNavigate } from '@/components/Link';
import { PredefinedBlock } from '@/components/PredefinedBlock';
import { RichText } from '@/components/RichText';
import { serializeLexicalChildNode } from '@/components/RichText/serialize-lexical';
import { SocialEmbed } from '@/components/SocialEmbed';
import { SocialProofs } from '@/components/SocialProofs';
import { TableSection } from '@/components/TableSection';
import Testimonials from '@/components/Testimonials';
import { TilesComponent } from '@/components/Tiles/TilesComponent';
import LandingFAQ from '@/pages/Landing/components/LandingFAQ';
import MicrositeLandingHeader from '@/pages/Landing/components/MicrositeLandingHeader';
import { AppRatingsBlaze } from '@/pages/PlanDetails/AppRatingsBlaze';
import { FeaturedCountry } from '@/services/resources/ResourceService';
import { getMediaUrl } from '@/utils/mediaUtils';
import { getPathnameWithLocale } from '@/utils/urlUtils';
import { FeaturedPlansBlockWithPlans } from '~/helpers/pageLayoutData';

const className = 'c-PageLayout';
const sectionClassName = `${className}__section`;
const spacingClassName = `${className}__spacing`;

type Props = {
  page: {
    layout?: Blocks;
    showNavbar?: boolean | null;
    showFooter?: boolean | null;
  };
  plans: ProductOffering[];
  featuredCountries?: FeaturedCountry[];
  testimonials: TestimonialItem[];
  howDoesItWork: HowDoesItWork | null;
  countries?: Country[];
  featuredCompaniesContent?: FeaturedCompany | null;
  locale?: string;
  featuredPlans?: FeaturedPlansBlockWithPlans[];
};

const isExternal = (to: string | To): to is string => {
  return typeof to === 'string' && to.startsWith('http');
};

export const PageLayout: FC<Props> = ({
  page,
  plans,
  featuredCountries,
  testimonials,
  howDoesItWork,
  countries,
  featuredCompaniesContent,
  locale,
  featuredPlans,
}) => {
  return (
    <Layout showNavBar={Boolean(page.showNavbar)}>
      <div className={className}>
        {page.layout && page.layout.length > 0 ? (
          <PageBlocks
            blocks={page.layout}
            plans={plans}
            testimonials={testimonials}
            featuredCompaniesContent={featuredCompaniesContent}
            howDoesItWork={howDoesItWork}
            locale={locale}
            countries={countries}
            featuredPlans={featuredPlans}
          />
        ) : null}
      </div>
      {page.showFooter ? (
        <Footer countries={countries} featuredCountries={featuredCountries} />
      ) : null}
    </Layout>
  );
};

export const PageBlocks = ({
  blocks,
  plans,
  testimonials,
  featuredCompaniesContent,
  howDoesItWork,
  appRatings,
  locale,
  countries,
  featuredPlans,
}: {
  blocks: NonNullable<Blocks>;
  plans: ProductOffering[];
  testimonials: TestimonialItem[];
  featuredCompaniesContent?: FeaturedCompany | null;
  howDoesItWork: HowDoesItWork | null;
  appRatings?: {
    android?: number;
    iOS?: number;
  };
  locale?: string;
  countries?: Country[];
  featuredPlans?: FeaturedPlansBlockWithPlans[];
}) => {
  const navigate = useNavigate();
  const analyticsContext = useAnalytics();

  const onCtaClick = (href: string, newTab: boolean) => {
    if (newTab) {
      if (isExternal(href)) {
        window.open(href, '_blank', 'noopener,noreferrer');
      } else {
        window.open(
          getPathnameWithLocale(href, locale),
          '_blank',
          'noopener,noreferrer',
        );
      }
    } else {
      if (isExternal(href)) {
        window.location.href = href;
      } else {
        navigate(href);
      }
    }
  };

  const onCtaTracking = (title: string) => {
    analyticsContext?.trackClickAction({
      element_id: 'cta',
      element_text: title,
    });
  };

  const renderCta = (
    cta: BlockCta | undefined,
    variant: 'primary' | 'secondary',
  ) => {
    if (!(cta && cta.title && cta.href)) {
      return undefined;
    }

    const { href, title, newTab } = cta;

    if (href.startsWith('mailto:')) {
      return (
        <a href={href}>
          <Button variant={variant}>{title}</Button>
        </a>
      );
    }

    return (
      <Button
        variant={variant}
        onClick={() => onCtaClick(href, newTab ?? false)}
      >
        {title}
      </Button>
    );
  };

  const renderLinkCta = (
    cta: BlockCta | undefined,
    { intent }: { intent?: ButtonV2Props['intent'] },
  ) => {
    if (!(cta && cta.title && cta.href)) {
      return null;
    }

    const { href, title, newTab } = cta;

    return (
      <ButtonV2
        variant="link"
        intent={intent}
        fluid
        onClick={() => onCtaTracking(title)}
      >
        <Link
          className={`${className}__link`}
          to={href}
          rel="noopener noreferrer"
          target={newTab ? '_blank' : '_self'}
        >
          {title}
        </Link>
      </ButtonV2>
    );
  };

  return blocks.map((block) => {
    const blockLayout = () => {
      switch (block.blockType) {
        case 'rich-text':
          return (
            <div
              key={block.id}
              className={classNames(sectionClassName, spacingClassName)}
            >
              {block.highlight?.isHighlighted ? (
                <HighlightBox
                  backgroundColor={block.highlight.backgroundColor}
                  borderColor={block.highlight.borderColor}
                >
                  <RichText content={block.content} data={{ plans }} />
                </HighlightBox>
              ) : (
                <RichText content={block.content} data={{ plans }} />
              )}
            </div>
          );

        case 'hero-block': {
          if (!block.mediaType) return null;

          const asset: HeroProps['asset'] = {
            placement: block.placement,
            ratio: block.ratio,
            type: block.mediaType,
            Component:
              block.mediaType === 'image' ? (
                <ImageMedia media={block.heroImage} />
              ) : (
                <SocialEmbed
                  type={block.embedType ?? ''}
                  url={block.embedUrl ?? ''}
                />
              ),
          };

          return (
            <div
              key={block.id}
              className={classNames(
                sectionClassName,
                `${sectionClassName}--wide`,
                spacingClassName,
              )}
            >
              <Hero
                Title={
                  <Title tag={2} level={1}>
                    {serializeLexicalChildNode({
                      content: block.title,
                    })}
                  </Title>
                }
                Body={<RichText content={block.text} />}
                asset={asset}
                ctas={{
                  PrimaryCta: renderCta(block.primaryCta, 'primary'),
                  SecondaryCta: renderCta(block.secondaryCta, 'secondary'),
                  placement: block.ctaPlacement,
                }}
              />
            </div>
          );
        }

        case 'microsite-hero-block': {
          return (
            <MicrositeLandingHeader
              key={block.id}
              countries={countries ?? []}
              content={block}
            />
          );
        }

        case 'predefined-block':
          return (
            <PredefinedBlock
              block={block}
              key={block.id}
              testimonials={testimonials}
              featuredCompaniesContent={featuredCompaniesContent}
              howDoesItWork={howDoesItWork}
            />
          );
        case 'tiles-block':
          return (
            <div
              key={block.id}
              className={classNames(
                sectionClassName,
                `${sectionClassName}--wide`,
              )}
            >
              <TilesComponent
                title={serializeLexicalChildNode({
                  content: block.title,
                })}
                subtitle={serializeLexicalChildNode({
                  content: block.subtitle,
                })}
                tiles={block.tiles.map((tile) => {
                  return {
                    title: serializeLexicalChildNode({
                      content: tile.title,
                    }),
                    subtitle: serializeLexicalChildNode({
                      content: tile.subtitle,
                    }),
                    image: <ImageMedia media={tile.image} />,
                  };
                })}
              />
            </div>
          );
        case 'faq-block': {
          const { faqs } = block;
          if (!Array.isArray(faqs)) {
            return null;
          }

          const parsedFaqs = faqs.map(({ question, answer }) => ({
            question,
            answer: <RichText content={answer} />,
          }));

          return (
            <div
              key={block.id}
              className={classNames(sectionClassName, spacingClassName)}
            >
              <LandingFAQ
                type="content-based"
                Title={serializeLexicalChildNode({
                  content: block.title,
                })}
                faqs={parsedFaqs}
              />
            </div>
          );
        }

        case 'plan-cards': {
          const blockPlanIds = block.plans.map(({ planId }) => planId);
          const planIdToPlan = new Map(plans.map((plan) => [plan.id, plan]));
          const list =
            blockPlanIds.map((id) => planIdToPlan.get(id) as ProductOffering) ||
            [];

          return (
            <div
              key={block.id}
              className={classNames(
                `${sectionClassName}--wide`,
                spacingClassName,
                `${className}__plans`,
              )}
            >
              <DynamicPlansSection
                useTitle={block.title?.useTitle}
                title={serializeLexicalChildNode({
                  content: block.title?.title,
                })}
                subtitle={serializeLexicalChildNode({
                  content: block.title?.subTitle,
                })}
                plans={list}
                cta={block.cta}
                showCarouselOnMobile={block.showCarouselOnMobile}
                carouselOptions={{ loop: false }}
              />
            </div>
          );
        }

        case 'info-banner-block': {
          return (
            <div
              key={block.id}
              className={classNames(
                sectionClassName,
                `${sectionClassName}--wide`,
                spacingClassName,
              )}
            >
              <InfoBanner
                Title={
                  <Title tag={2}>
                    {serializeLexicalChildNode({
                      content: block.title,
                    })}
                  </Title>
                }
                titlePlacement={block.titlePlacement}
                Body={<RichText content={block.body} data={{ plans }} />}
                PrimaryCta={renderCta(block.primaryCta, 'primary')}
                SecondaryCta={renderCta(block.secondaryCta, 'secondary')}
              />
            </div>
          );
        }

        case 'image-with-background-block': {
          return (
            <div
              key={block.id}
              className={classNames(
                sectionClassName,
                `${sectionClassName}--wide`,
              )}
            >
              <AppRatingsBlaze appRatings={appRatings} />
              <ImageWithBackgroundBlock
                Title={
                  <Title level={{ sm: 4, md: 2, lg: 1 }} tag={2}>
                    {serializeLexicalChildNode({ content: block.title })}
                  </Title>
                }
                Cta={renderLinkCta(block.cta, { intent: 'neutral-gray' })}
                MainImage={
                  block.mainImage && (
                    <img
                      alt={block.blockType}
                      src={getMediaUrl(block.mainImage)}
                    />
                  )
                }
              />
            </div>
          );
        }

        case 'social-proofs': {
          return (
            <div
              key={block.id}
              className={classNames(
                sectionClassName,
                `${sectionClassName}--wide`,
                spacingClassName,
              )}
            >
              <SocialProofs socialProofs={block}></SocialProofs>
            </div>
          );
        }

        case 'table':
          return (
            <div
              key={block.id}
              className={classNames(
                sectionClassName,
                `${sectionClassName}--wide`,
                spacingClassName,
              )}
            >
              <TableSection data={block} />
            </div>
          );

        case 'testimonials':
          return (
            <div
              key={block.id}
              className={classNames(
                sectionClassName,
                `${sectionClassName}--wide`,
                spacingClassName,
              )}
            >
              <Testimonials testimonialsArray={block.testimonials || []} />
            </div>
          );

        case 'featured-plans-block': {
          const current = featuredPlans?.find(
            (featuredPlanBlock) =>
              block.countryCode === featuredPlanBlock.countryCode,
          );
          if (!current) return null;

          const sortedPlans = sortProductOfferingsBy('default', current?.plans);

          return (
            <div key={block.id} className={spacingClassName}>
              <DynamicPlansSection
                useTitle={block.title?.useTitle}
                title={serializeLexicalChildNode({
                  content: block.title?.title,
                })}
                subtitle={serializeLexicalChildNode({
                  content: block.title?.subTitle,
                })}
                plans={sortedPlans.featured}
                cta={current?.cta}
                showCarouselOnMobile={block.showCarouselOnMobile}
              />
            </div>
          );
        }
        default:
          return null;
      }
    };

    const BlockLayout = blockLayout();
    if ('backgroundColor' in block && block.backgroundColor) {
      return (
        <div
          key={block.id}
          style={{ background: `var(${block.backgroundColor})` }}
        >
          {BlockLayout}
        </div>
      );
    }

    return BlockLayout;
  });
};
